import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { Input, Button, Popover, Modal, Spin, message, Tour } from 'antd';
import type { TourProps } from 'antd';
import EmojiPicker from 'emoji-picker-react';
import s from './s.module.less';
import { useFileUpload, FileUploadResult } from 'hooks/useFileUpload';
import { TFile } from 'types/inbox';
import { getProviderPrivateFileSigned, uploadProviderPrivateFile } from 'api/common';
import { getPrivateFilePreview, uploadOpsPrivateFile, opsFetchAiCtaResult } from 'api/operation';
import { AdjustLengthIcon, ChangeToneIcon, TellAvaIcon } from 'components/MenuIcons';
import { EAIEditorType, TFetchAiCtaResultRes } from 'types/ai';
import { fetchAiCtaResult } from 'api/ai';
import { getHasShowChatInputTour, setHasShowChatInputTour } from 'utils/localstore';
import DocumentsTable from 'pages/dashboard/ProspectsDetailPage/components/DocumentTab/components/DocumentsTable';
import { IGetDocumentItem } from 'types/document';
import useGetDocument from 'hooks/useGetDocument';

const { TextArea } = Input;

interface IFileUploadResult extends TFile {
    id: number;
}

export interface ChatInputValue {
    text: string;
    files: FileUploadResult[];
}

type THistory = {
    title: string;
    content: string;
};

interface Props {
    value?: ChatInputValue;
    onChange?: (value: ChatInputValue) => void;
    onSend?: (value: ChatInputValue) => void;
    placeholder?: string;
    disabled?: boolean;
    maxFiles?: number;
    type?: EAIEditorType;
    showNewTip?: boolean;
    hideSend?: boolean;
    clientId: number;
    isOps?: boolean;
}

const AIButtons = [
    {
        label: 'Adjust length',
        icon: AdjustLengthIcon,
        tooltip: 'Adjust content length',
        options: [
            {
                label: 'Simplify',
                ctaId: 1,
            },
            {
                label: 'Rephrase',
                ctaId: 2,
            },
            {
                label: 'Elaborate',
                ctaId: 3,
            },
        ],
    },
    {
        label: 'Change tone',
        icon: ChangeToneIcon,
        tooltip: 'Change writing tone',
        options: [
            {
                label: 'Casual',
                ctaId: 4,
            },
            {
                label: 'Friendly',
                ctaId: 7,
            },
            {
                label: 'Empathetic',
                ctaId: 5,
            },
            {
                label: 'Formal',
                ctaId: 6,
            },
        ],
    },
    {
        label: 'Tell Ava to...',
        icon: TellAvaIcon,
        tooltip: 'Edit with an instruction',
        placeholder: 'For example: make it sounds joyful',
        ctaId: 12,
    },
];

const ChatInput: React.FC<Props> = ({
    value = { text: '', files: [] },
    onChange,
    onSend,
    placeholder = 'Type your message here...',
    disabled = false,
    maxFiles = 5,
    type = EAIEditorType.ABOUTME,
    showNewTip,
    hideSend = false,
    clientId,
    isOps = false,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const textAreaRef = useRef<any>(null);
    const editorWrapperRef = useRef<HTMLDivElement>(null);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showFileMenu, setShowFileMenu] = useState(false);
    const [showAIMainMenu, setShowAIMainMenu] = useState(false);
    const [aiPrompt, setAiPrompt] = useState<string>('');
    const [isAIFetching, setIsAIFetching] = useState(false);
    const [showDocumentsModal, setShowDocumentsModal] = useState(false);
    const [historyList, setHistoryList] = useState<THistory[]>([]);
    const [showHistory, setShowHistory] = useState(false);
    const [tourOpen, setTourOpen] = useState(false);
    const [selectedDocuments, setSelectedDocuments] = useState<IGetDocumentItem[]>([]);
    const hasHistory = !!(historyList && historyList.length > 0);

    const [documents, hasDocuments] = useGetDocument(clientId, !isOps);

    const { loading: fileUploading, uploadFile } = useFileUpload({
        accept: '.pdf,.jpg,.jpeg,.png,.gif,.webp',
        maxSize: 10,
        formData: {
            uploadTypeEnum: isOps ? 'OPS_PRIVATE' : 'PROVIDER_PRIVATE',
        },
        uploadFn: isOps ? uploadOpsPrivateFile : uploadProviderPrivateFile,
        formatFileData: async (result: IFileUploadResult) => {
            const data: FileUploadResult = {
                id: result.id,
                url: result.fileUri,
                name: result.fileOriginName,
                type: result.fileExtension,
            };
            const fn = isOps ? getPrivateFilePreview : getProviderPrivateFileSigned;
            const res = await fn(result.fileUri);
            if (!res.error) {
                data.previewUrl = res.data?.data?.singedUrl;
            }
            return data;
        },
    });

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange?.({
            ...value,
            text: e.target.value,
        });
    };

    const handleEmojiSelect = (emoji: any) => {
        const emojiText = emoji.emoji;
        const currentText = value.text || '';
        const textarea = textAreaRef.current?.resizableTextArea?.textArea;
        if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const newText = currentText.substring(0, start) + emojiText + currentText.substring(end);
            onChange?.({
                ...value,
                text: newText,
            });
            // 恢复光标位置
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start + emojiText.length, start + emojiText.length);
            }, 0);
        } else {
            onChange?.({
                ...value,
                text: currentText + emojiText,
            });
        }
        setShowEmojiPicker(false);
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = e.target.files;
        if (!selectedFiles) return;

        // 检查文件数量限制
        if (value.files.length + selectedFiles.length > maxFiles) {
            message.error(`You can only upload ${maxFiles} files at a time`);
            return;
        }

        // 并行上传所有文件
        const uploadPromises = Array.from(selectedFiles).map(async (file) => {
            const result = await uploadFile(file);
            return result;
        });

        const uploadedFiles = (await Promise.all(uploadPromises)).filter((f): f is FileUploadResult => f !== null);

        if (uploadedFiles.length > 0) {
            onChange?.({
                ...value,
                files: [...value.files, ...uploadedFiles],
            });
        }

        // 清空 input 以便重复选择同一文件
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        setShowFileMenu(false);
    };

    const handleRemoveFile = (fileId: number) => {
        onChange?.({
            ...value,
            files: value.files.filter((f) => f.id !== fileId),
        });
    };

    const handleUploadFromDevice = () => {
        setShowFileMenu(false);
        fileInputRef.current?.click();
    };

    const handleUploadFromDocuments = () => {
        setShowFileMenu(false);
        setShowDocumentsModal(true);
    };

    const addToHistory = (item: THistory) => {
        const newHistoryList = [...historyList, item];
        setHistoryList(newHistoryList);
    };

    const handleSend = () => {
        if (disabled || (!value.text.trim() && value.files.length === 0)) return;
        onSend?.(value);
    };

    const triggerAiAction = async (rewriteAction: string, ctaId: number) => {
        if (!value.text.trim()) {
            message.warning('Please enter some text first');
            return;
        }

        const requestData: TFetchAiCtaResultRes = {
            ctaId,
            type,
            content: value.text,
            prompt: ctaId === 12 ? aiPrompt : undefined,
        };

        setIsAIFetching(true);
        setShowAIMainMenu(false);

        try {
            const fn = isOps ? opsFetchAiCtaResult : fetchAiCtaResult;
            const result = await fn(requestData);
            if (!result.error) {
                const resultData = result.data?.data;
                if (resultData?.content) {
                    onChange?.({
                        ...value,
                        text: resultData.content,
                    });

                    // 添加到历史记录
                    addToHistory({
                        title: ctaId === 12 ? aiPrompt : rewriteAction,
                        content: resultData.content,
                    });
                }
            }
        } catch (error) {
            console.error('AI action error:', error);
            message.error('Failed to process AI action');
        } finally {
            setIsAIFetching(false);
            setAiPrompt('');
        }
    };

    const hasContent = value.text.trim() || value.files.length > 0;

    // Tour 配置
    const steps: TourProps['steps'] = [
        {
            title: ' ',
            description: "We've moved the AI editors here. You can also insert emojis or send files to patients now.",
            target: () => editorWrapperRef.current || document.body,
            placement: 'top',
            nextButtonProps: {
                children: 'Got it',
            },
        },
    ];

    // 监听 showNewTip 变化，显示 Tour
    useEffect(() => {
        if (showNewTip && !getHasShowChatInputTour() && !isOps) {
            // 延迟显示，确保 DOM 已渲染
            setTimeout(() => {
                setTourOpen(true);
            }, 500);
        }
    }, [showNewTip, isOps]);

    const HistoryContent = () => {
        return (
            <div className={s.historyWrapPop}>
                <div className={s.historyTitle}>Choose a version from the history</div>
                <div className={s.historyContent}>
                    {historyList?.map((item: THistory, inx) => {
                        return (
                            <div className={s.historyItem} key={`${item.title}_${inx}`}>
                                <div className={s.historyItemTitle}>{item.title}</div>
                                <div className={s.historyItemContent}>{item.content}</div>
                                <div className={s.historyItemBtn}>
                                    <Button
                                        onClick={() => {
                                            onChange?.({
                                                ...value,
                                                text: item.content,
                                            });
                                            setShowHistory(false);
                                        }}
                                        className={s.btn}
                                        size="small"
                                    >
                                        Select
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const emojiPickerContent = (
        <div className={s.emojiPickerWrap}>
            <EmojiPicker onEmojiClick={handleEmojiSelect} lazyLoadEmojis />
        </div>
    );

    const fileMenuContent = (
        <div className={s.fileMenu}>
            <div className={s.fileMenuItem} onClick={handleUploadFromDevice}>
                Upload from device
            </div>
            {clientId && hasDocuments && (
                <div className={s.fileMenuItem} onClick={handleUploadFromDocuments}>
                    Upload from "Documents"
                </div>
            )}
        </div>
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const getPreviewUrl = async (documentUrl: string) => {
        try {
            const fn = isOps ? getPrivateFilePreview : getProviderPrivateFileSigned;
            const res = await fn(documentUrl);
            if (!res.error && res.data?.data?.singedUrl) {
                return res.data?.data?.singedUrl;
            }
            return undefined;
        } catch (error) {
            console.error('getPreviewUrl error:', error);
            return undefined;
        }
    };

    const handleSelectDocuments = async () => {
        const addedFiles: FileUploadResult[] = await Promise.all(
            selectedDocuments.map(async (item) => {
                const previewUrl = await getPreviewUrl(item.documentUrl);
                return {
                    id: item.fileUploadId,
                    name: item.fileOriginName,
                    type: item.fileType,
                    url: item.documentUrl,
                    previewUrl,
                };
            }),
        );
        console.log('addedFiles', addedFiles);
        onChange?.({ ...value, files: [...value.files, ...addedFiles] });
    };

    // AI 主菜单内容
    const aiMainMenuContent = useMemo(() => {
        return (
            <div className={s.aiMainMenu}>
                {/* Adjust length 选项 */}
                <Popover
                    content={
                        <div className={s.aiSubMenu}>
                            {AIButtons[0].options?.map((option) => (
                                <div
                                    key={option.label}
                                    onClick={() => {
                                        triggerAiAction(option.label, option.ctaId);
                                        setShowAIMainMenu(false);
                                    }}
                                    className={s.aiSubMenuItem}
                                >
                                    {option.label}
                                </div>
                            ))}
                        </div>
                    }
                    trigger="hover"
                    placement="rightTop"
                    overlayClassName={s.aiSubMenuPopover}
                >
                    <div className={s.aiMainMenuItem}>
                        <span>{AIButtons[0].label}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                                d="M6 12L10 8L6 4"
                                stroke="#6B7280"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                </Popover>

                {/* Change tone 选项 */}
                <Popover
                    content={
                        <div className={s.aiSubMenu}>
                            {AIButtons[1].options?.map((option) => (
                                <div
                                    key={option.label}
                                    onClick={() => {
                                        triggerAiAction(option.label, option.ctaId);
                                        setShowAIMainMenu(false);
                                    }}
                                    className={s.aiSubMenuItem}
                                >
                                    {option.label}
                                </div>
                            ))}
                        </div>
                    }
                    trigger="hover"
                    placement="rightTop"
                    overlayClassName={s.aiSubMenuPopover}
                >
                    <div className={s.aiMainMenuItem}>
                        <span>{AIButtons[1].label}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path
                                d="M6 12L10 8L6 4"
                                stroke="#6B7280"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                </Popover>

                {/* Tell Ava to... 选项 */}
                <div className={s.aiMainMenuItemNoHover}>
                    <div className={s.aiTextTitle}>{AIButtons[2].label}</div>
                    <div className={s.aiTextInputWrap}>
                        <Input.TextArea
                            rows={5}
                            placeholder={AIButtons[2].placeholder}
                            value={aiPrompt}
                            onChange={(e) => {
                                setAiPrompt(e.target.value);
                            }}
                        />
                    </div>
                    <div className={s.aiTextSubmit}>
                        <Button
                            onClick={() => {
                                triggerAiAction(AIButtons[2].label, AIButtons[2].ctaId!);
                                setShowAIMainMenu(false);
                            }}
                            disabled={!aiPrompt}
                            type="primary"
                        >
                            Submit
                        </Button>
                    </div>
                </div>
            </div>
        );
    }, [aiPrompt, value.text, type]);

    return (
        <div className={`${s.chatInput} ${disabled ? s.disabled : ''}`}>
            {/* 文件预览区域 */}
            {value.files.length > 0 && (
                <div className={s.filesPreview}>
                    {value.files.map((file) => (
                        <div key={file.id} className={s.fileItem}>
                            {file.type === 'pdf' ? (
                                <div className={s.pdfTag}>
                                    <svg
                                        className={s.pdfIcon}
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                    >
                                        <path
                                            d="M9.33333 1.33334H4.00001C3.6464 1.33334 3.30724 1.47382 3.05719 1.72387C2.80715 1.97392 2.66667 2.31305 2.66667 2.66668V13.3333C2.66667 13.687 2.80715 14.0261 3.05719 14.2762C3.30724 14.5262 3.6464 14.6667 4.00001 14.6667H12C12.3536 14.6667 12.6928 14.5262 12.9428 14.2762C13.1929 14.0261 13.3333 13.687 13.3333 13.3333V5.33334L9.33333 1.33334Z"
                                            stroke="white"
                                            strokeWidth="1.33333"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M9.33333 1.33334V5.33334H13.3333"
                                            stroke="white"
                                            strokeWidth="1.33333"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <span className={s.pdfName}>{file.name}</span>
                                    <Button className={s.removeBtn} onClick={() => handleRemoveFile(file.id!)}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="14"
                                            height="14"
                                            viewBox="0 0 14 14"
                                            fill="none"
                                        >
                                            <path
                                                d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                                                stroke="white"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </Button>
                                </div>
                            ) : (
                                <div className={s.imagePreview}>
                                    <img src={file.previewUrl} alt={file.name} />
                                    <Button className={s.imageRemoveBtn} onClick={() => handleRemoveFile(file.id!)}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="12"
                                            height="12"
                                            viewBox="0 0 12 12"
                                            fill="none"
                                        >
                                            <path
                                                d="M9 3L3 9M3 3L9 9"
                                                stroke="white"
                                                strokeWidth="1.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </Button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* 文本输入区域 */}
            <div className={s.inputWrapper}>
                <TextArea
                    ref={textAreaRef}
                    className={s.textInput}
                    value={value.text}
                    onChange={handleTextChange}
                    placeholder={placeholder}
                    disabled={disabled || isAIFetching}
                    rows={4}
                    onKeyDown={handleKeyDown}
                />

                {/* 工具栏 */}
                <div className={s.toolbar}>
                    <div className={s.leftActions} ref={editorWrapperRef}>
                        {/* Emoji 按钮 */}
                        <Popover
                            content={emojiPickerContent}
                            trigger="click"
                            open={showEmojiPicker}
                            onOpenChange={setShowEmojiPicker}
                            placement="topLeft"
                            overlayClassName={s.emojiPopover}
                        >
                            <Button
                                className={s.iconBtn}
                                disabled={disabled}
                                type="text"
                                icon={
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                    >
                                        <path
                                            d="M7.64298 12.357C8.94473 13.6588 11.0553 13.6588 12.357 12.357M12.5 8.33333H12.4917M7.5 8.33333H7.49167M2.5 10C2.5 14.1421 5.85786 17.5 10 17.5C14.1421 17.5 17.5 14.1421 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C5.85786 2.5 2.5 5.85786 2.5 10Z"
                                            stroke="#6B7280"
                                            strokeWidth="1.66667"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                }
                            />
                        </Popover>

                        {/* 文件上传按钮 */}
                        <Popover
                            content={fileMenuContent}
                            trigger="click"
                            open={showFileMenu}
                            onOpenChange={setShowFileMenu}
                            placement="topLeft"
                            overlayClassName={s.filePopover}
                        >
                            <Button
                                className={s.iconBtn}
                                disabled={disabled || fileUploading}
                                type="text"
                                icon={
                                    fileUploading ? (
                                        <Spin size="small" />
                                    ) : (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M8 4C6.34315 4 5 5.34315 5 7V11C5 13.7614 7.23858 16 10 16C12.7614 16 15 13.7614 15 11V7C15 6.44772 15.4477 6 16 6C16.5523 6 17 6.44772 17 7V11C17 14.866 13.866 18 10 18C6.13401 18 3 14.866 3 11V7C3 4.23858 5.23858 2 8 2C10.7614 2 13 4.23858 13 7V11C13 12.6569 11.6569 14 10 14C8.34315 14 7 12.6569 7 11V7C7 6.44772 7.44772 6 8 6C8.55228 6 9 6.44772 9 7V11C9 11.5523 9.44772 12 10 12C10.5523 12 11 11.5523 11 11V7C11 5.34315 9.65685 4 8 4Z"
                                                fill="#6B7280"
                                            />
                                        </svg>
                                    )
                                }
                            />
                        </Popover>

                        <Popover
                            content={aiMainMenuContent}
                            trigger="click"
                            open={showAIMainMenu}
                            onOpenChange={setShowAIMainMenu}
                            placement="topLeft"
                            overlayClassName={s.aiMainMenuPopover}
                        >
                            <Button
                                className={s.iconBtn}
                                disabled={disabled}
                                loading={isAIFetching}
                                type="text"
                                icon={
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                    >
                                        <path
                                            d="M6.89518 6.43758L4.39518 3.93758L5.58268 2.75008L8.08268 5.25008L6.89518 6.43758ZM9.99935 4.45841V0.916748H11.666V4.45841H9.99935ZM16.0618 15.6042L13.5618 13.1042L14.7493 11.9167L17.2493 14.4167L16.0618 15.6042ZM14.7493 6.43758L13.5618 5.25008L16.0618 2.75008L17.2493 3.93758L14.7493 6.43758ZM15.541 10.0001V8.33341H19.0827V10.0001H15.541ZM4.27018 18.0834L1.91602 15.7292C1.74935 15.5626 1.66602 15.3681 1.66602 15.1459C1.66602 14.9237 1.74935 14.7292 1.91602 14.5626L9.47852 6.97925C9.96463 6.49314 10.5549 6.25008 11.2493 6.25008C11.9438 6.25008 12.5341 6.49314 13.0202 6.97925C13.5063 7.46536 13.7493 8.05564 13.7493 8.75008C13.7493 9.44453 13.5063 10.0348 13.0202 10.5209L5.43685 18.0834C5.27018 18.2501 5.07574 18.3334 4.85352 18.3334C4.63129 18.3334 4.43685 18.2501 4.27018 18.0834ZM10.0827 11.1042L9.7806 10.8126L9.47852 10.5209L9.18685 10.2292L8.89518 9.93758L9.47852 10.5209L10.0827 11.1042ZM4.85352 16.3334L10.0827 11.1042L8.89518 9.93758L3.68685 15.1459L4.85352 16.3334Z"
                                            fill="#6B7280"
                                        />
                                    </svg>
                                }
                            />
                        </Popover>

                        {!isAIFetching && hasHistory && (
                            <Popover
                                trigger="click"
                                open={showHistory}
                                onOpenChange={(v) => {
                                    setShowHistory(v);
                                }}
                                arrow={false}
                                placement="topLeft"
                                title=""
                                content={<HistoryContent />}
                            >
                                <div className={s.historyWrap}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                    >
                                        <g clipPath="url(#clip0_3081_10987)">
                                            <path
                                                d="M8.66667 4C8.48986 4 8.32029 4.07024 8.19526 4.19526C8.07024 4.32029 8 4.48986 8 4.66667V8.33333C8 8.51014 8.07024 8.67971 8.19526 8.80474C8.32029 8.92976 8.48986 9 8.66667 9H12C12.1768 9 12.3464 8.92976 12.4714 8.80474C12.5964 8.67971 12.6667 8.51014 12.6667 8.33333C12.6667 8.15652 12.5964 7.98695 12.4714 7.86193C12.3464 7.7369 12.1768 7.66667 12 7.66667H9.47333C9.42913 7.66667 9.38674 7.64911 9.35548 7.61785C9.32423 7.5866 9.30667 7.5442 9.30667 7.5V4.66667C9.3068 4.49438 9.24024 4.32873 9.12092 4.20445C9.00161 4.08016 8.83882 4.00689 8.66667 4Z"
                                                fill="#6B7280"
                                            />
                                            <path
                                                d="M8.3801 0.839636C6.94581 0.895524 5.56088 1.37844 4.40282 2.2265C3.24476 3.07456 2.36632 4.24912 1.8801 5.59964C1.86679 5.6353 1.84167 5.66533 1.80892 5.68473C1.77618 5.70414 1.73777 5.71175 1.7001 5.7063L0.573435 5.53964C0.511419 5.53132 0.448308 5.53994 0.390796 5.56459C0.333284 5.58924 0.283515 5.62899 0.246768 5.67964C0.21439 5.73288 0.197266 5.79399 0.197266 5.8563C0.197266 5.91861 0.21439 5.97973 0.246768 6.03297L2.0001 9.1663C2.02785 9.2135 2.06658 9.2533 2.11301 9.28232C2.15944 9.31134 2.21218 9.32871 2.26677 9.33297C2.31741 9.33852 2.36865 9.33305 2.41698 9.31694C2.46531 9.30083 2.50958 9.27446 2.54677 9.23964L5.0601 6.73964C5.10412 6.69602 5.13512 6.641 5.1496 6.58075C5.16408 6.5205 5.16149 6.4574 5.1421 6.39855C5.12271 6.33969 5.0873 6.2874 5.03984 6.24756C4.99238 6.20771 4.93476 6.18188 4.87343 6.17297L3.72677 5.99964C3.70161 5.99495 3.6778 5.98474 3.65705 5.96976C3.6363 5.95477 3.61913 5.93538 3.60677 5.91297C3.59507 5.89029 3.58897 5.86515 3.58897 5.83964C3.58897 5.81412 3.59507 5.78898 3.60677 5.7663C4.00375 4.86709 4.63418 4.09053 5.43261 3.51724C6.23105 2.94394 7.16838 2.59481 8.14731 2.50608C9.12623 2.41736 10.1111 2.59228 10.9996 3.01268C11.8881 3.43309 12.6478 4.08365 13.2 4.89683C13.7522 5.71001 14.0766 6.65617 14.1397 7.63709C14.2027 8.618 14.0021 9.59791 13.5585 10.4751C13.115 11.3523 12.4447 12.0947 11.6174 12.6254C10.79 13.1561 9.83566 13.4556 8.85343 13.493C8.63242 13.5009 8.42362 13.5964 8.27297 13.7583C8.12231 13.9202 8.04214 14.1353 8.0501 14.3563C8.05806 14.5773 8.15349 14.7861 8.31539 14.9368C8.4773 15.0874 8.69242 15.1676 8.91343 15.1596C10.8133 15.0898 12.6075 14.2681 13.9015 12.8753C15.1955 11.4826 15.8833 9.6328 15.8134 7.73297C15.7436 5.83313 14.9219 4.03886 13.5291 2.74486C12.1364 1.45086 10.2866 0.763129 8.38677 0.832969L8.3801 0.839636Z"
                                                fill="currentColor"
                                            />
                                        </g>
                                        <defs>
                                            <clipPath id="clip0_3081_10987">
                                                <rect width="16" height="16" fill="white" />
                                            </clipPath>
                                        </defs>
                                    </svg>
                                    <span className={s.view}>View history</span>
                                </div>
                            </Popover>
                        )}
                    </div>

                    {/* 发送按钮 */}
                    {!hideSend && (
                        <Button
                            className={`${s.sendBtn} ${hasContent ? s.active : ''}`}
                            onClick={handleSend}
                            disabled={disabled || !hasContent}
                            type="text"
                            icon={
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                >
                                    <path
                                        d="M20.2346 5.68609C20.6666 4.49109 19.5086 3.33309 18.3136 3.76609L3.70855 9.04809C2.50955 9.48209 2.36455 11.1181 3.46755 11.7571L8.12955 14.4561L12.2926 10.2931C12.4812 10.1109 12.7338 10.0101 12.996 10.0124C13.2582 10.0147 13.509 10.1199 13.6944 10.3053C13.8798 10.4907 13.9849 10.7415 13.9872 11.0037C13.9895 11.2659 13.8887 11.5185 13.7066 11.7071L9.54355 15.8701L12.2436 20.5321C12.8816 21.6351 14.5176 21.4891 14.9516 20.2911L20.2346 5.68609Z"
                                        fill={hasContent ? '#0E62F4' : '#9CA3AF'}
                                    />
                                </svg>
                            }
                        />
                    )}
                </div>
            </div>

            {/* 隐藏的文件输入 */}
            <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />

            {/* Documents 弹窗 */}
            {!isOps && (
                <Modal
                    title="Select files from Documents"
                    open={showDocumentsModal}
                    onCancel={() => {
                        setShowDocumentsModal(false);
                        setSelectedDocuments([]);
                    }}
                    onOk={() => {
                        setShowDocumentsModal(false);
                        handleSelectDocuments();
                    }}
                    centered
                    width="60vw"
                    bodyStyle={{
                        maxHeight: 'calc(80vh - 110px)',
                        overflowY: 'auto',
                        padding: '24px',
                    }}
                >
                    <div className={s.documentsModalContent}>
                        <DocumentsTable
                            clientId={clientId}
                            onUpload={() => setShowDocumentsModal(true)}
                            showUploadButton={false}
                            selectable
                            hideEdit
                            onSelectRows={setSelectedDocuments}
                        />
                    </div>
                </Modal>
            )}

            {/* Tour 提示 */}
            <Tour
                open={tourOpen}
                onClose={() => {
                    setTourOpen(false);
                    setHasShowChatInputTour(true);
                }}
                steps={steps}
            />
        </div>
    );
};

export default ChatInput;
